import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { apiPatch } from '../../lib/api_v2';
import { logError } from '../../lib/logger';
import { getMetaContent } from '../../lib/meta';

import { responseValidations } from '../settings/account/accountStyles';
import { SETTINGS_MODAL_SAVE_PILL_CLASS } from './settingsModalChrome';
import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import WorkspaceSlugInput, { cleanSlug } from './WorkspaceSlugInput';

// The backend's uniqueness message is "has already been taken" — reword to product copy.
const formatSlugErrors = (messages) =>
  (Array.isArray(messages) ? messages : [messages]).map((message) => (/taken/i.test(message) ? 'URL already taken' : message));

// The "Chat URL" section page: URL field with grayed host suffix, helper line, and a
// Save pill pinned into the modal's top bar (absolute against the Modal chrome).
const ChatUrlPanel = ({ onSaved }) => {
  const { workspace } = useRouteWorkspace();
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [originalSlug, setOriginalSlug] = useState('');
  const [slug, setSlug] = useState('');
  const workspaceSlug = workspace?.slug || '';
  const defaultHost = useMemo(() => getMetaContent('env:default_host'), []);

  useEffect(() => {
    setSlug(workspaceSlug);
    setOriginalSlug(workspaceSlug);
  }, [workspaceSlug]);

  const handleSlugChange = useCallback((cleanedValue) => {
    setSlug(cleanedValue);
    setErrors((prev) => (prev.slug ? { ...prev, slug: null } : prev));
  }, []);

  const handleSave = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!workspace?.id) return;

      const cleaned = cleanSlug(slug, defaultHost);
      setSlug(cleaned);

      if (cleaned !== originalSlug) {
        const confirmMessage =
          `Changing the chat URL will invalidate all invite and share links. ` +
          `Are you sure you want to change it from "${originalSlug}.${defaultHost}" to "${cleaned}.${defaultHost}"?`;
        if (!window.confirm(confirmMessage)) return;
      }

      setIsSaving(true);
      setErrors({});

      try {
        const response = await apiPatch(`/workspaces/${workspace.id}`, { workspace: { slug: cleaned } });
        const data = await response.json();

        if (response.ok && data.ok === true) {
          setOriginalSlug(cleaned);
          // Popping back to the settings root doubles as the success feedback
          onSaved?.();
        } else {
          const validation_errors = responseValidations(data);
          if (validation_errors?.slug) validation_errors.slug = formatSlugErrors(validation_errors.slug);
          setErrors(validation_errors || { general: 'Unable to save the chat URL. Please try again.' });
        }
      } catch (error) {
        logError('ChatUrlPanel', 'Failed to save workspace:', error);
        setErrors({ general: 'Failed to save the chat URL. Please try again.' });
      } finally {
        setIsSaving(false);
      }
    },
    [slug, originalSlug, defaultHost, onSaved, workspace?.id],
  );

  if (!workspace) return null;

  return (
    <form onSubmit={handleSave} className="w-full pt-10">
      {/* Sits in the modal top bar, opposite the back button */}
      <button
        type="submit"
        data-app-region="none"
        disabled={isSaving || !workspace?.id}
        className={SETTINGS_MODAL_SAVE_PILL_CLASS}
      >
        {isSaving ? '...' : 'Save'}
      </button>

      {errors.general && <div className="mb-5 rounded-md bg-red-50 p-4 text-sm text-red-800">{errors.general}</div>}

      <label htmlFor="slug" className="block text-cursed text-black dark:text-neutral-100">
        URL
      </label>
      <p className="mt-1 text-cursed leading-[18px] text-gray-500 dark:text-neutral-500">
        People can request to join your chat using this link
      </p>
      <div className="mt-2">
        <WorkspaceSlugInput
          id="slug"
          name="slug"
          value={slug}
          onChange={handleSlugChange}
          required
          spellCheck={false}
          errorMessages={errors.slug}
          textClassName="text-[15px] leading-5"
          className="h-12 rounded-lg border sm:border-[0.5px] border-white bg-gray-50 px-4 hover:border-white hover:bg-white focus:border-white"
        />
      </div>
    </form>
  );
};

export default ChatUrlPanel;
